import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  Tooltip,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import {
  type CreateFinancePayload,
  type FinanceBalance,
  type FinanceRecord,
  type FinanceSummaryItem,
  type FinanceType,
  type CreateRecurringExpensePayload,
  type RecurringExpense,
  createFinanceRecord,
  patchFinanceRecord,
  deleteFinanceRecord,
  createRecurringExpense,
  fetchFinanceList,
  fetchRecurringExpenses,
  fetchFinanceSummary,
  patchRecurringExpense,
  getFinanceAttachmentUrl,
} from './financeApi';
import {
  EXPENSE_CATEGORIES,
  FINANCE_TYPE_OPTIONS,
  PAYMENT_METHODS,
  DEFAULT_PAYMENT_METHOD,
  FINANCE_DATA_START_MONTH,
  isPaymentMethod,
  isAutoRecurringRecord,
  formatFinanceRecordMemo,
  getFinanceAmountClass,
  getFinanceCategories,
  getFinanceTypeBadgeClass,
  getFinanceTypeLabel,
} from './financeTypeUtils';
import { getAdminSession } from '../adminSession';
import './FinanceDashboard.scss';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

type FinanceFilter = 'ALL' | FinanceType;
type FinanceTab = 'accounting' | 'monthly' | 'category' | 'recurring';
type AccountingView = 'income' | 'balance';
type SelectedCategory = { category: string; type: 'REVENUE' | 'EXPENSE' };

const EMPTY_FORM: CreateFinancePayload = {
  type: 'REVENUE',
  category: '',
  payment_method: DEFAULT_PAYMENT_METHOD,
  amount: 0,
  description: '',
  transaction_date: '',
  cash_flow_date: '',
};

function todayDateString() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const today = `${y}-${m}-${day}`;
  const minDate = `${FINANCE_DATA_START_MONTH}-01`;
  return today < minDate ? minDate : today;
}

function createEmptyRecurringForm(): CreateRecurringExpensePayload {
  return {
    category: '',
    payment_method: DEFAULT_PAYMENT_METHOD,
    amount: 0,
    repeat_day: 1,
    start_date: todayDateString(),
    description: '',
  };
}

function numberKRW(value: number) {
  return value.toLocaleString('ko-KR', { style: 'currency', currency: 'KRW', maximumFractionDigits: 0 });
}

function formatNumberInput(value: number) {
  if (!Number.isFinite(value) || value <= 0) return '';
  return value.toLocaleString('ko-KR');
}

function parseNumberInput(value: string) {
  const digitsOnly = value.replace(/[^\d]/g, '');
  if (!digitsOnly) return 0;
  return Number(digitsOnly);
}

function currentMonthPrefix() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const month = `${y}-${m}`;
  return month < FINANCE_DATA_START_MONTH ? FINANCE_DATA_START_MONTH : month;
}

function shiftMonth(month: string, delta: number) {
  const [year, monthNum] = month.split('-').map(Number);
  const d = new Date(year, monthNum - 1 + delta, 1);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const next = `${y}-${m}`;
  if (next < FINANCE_DATA_START_MONTH) return FINANCE_DATA_START_MONTH;
  return next;
}

function canShiftMonthBack(month: string) {
  return month > FINANCE_DATA_START_MONTH;
}

function formatMonthLabel(month: string) {
  const [year, monthNum] = month.split('-');
  return `${year}년 ${Number(monthNum)}월`;
}

const WEEKDAY_LABELS = ['일요일', '월요일', '화요일', '수요일', '목요일', '금요일', '토요일'];

function getRecordDateKey(row: FinanceRecord) {
  return row.transaction_date || row.cash_flow_date || '';
}

function formatDayHeader(dateKey: string) {
  if (!dateKey) return '날짜 없음';
  const [y, m, d] = dateKey.split('-').map(Number);
  if (!y || !m || !d) return dateKey;
  const date = new Date(y, m - 1, d);
  const today = new Date();
  const isToday =
    date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth() &&
    date.getDate() === today.getDate();
  if (isToday) return '오늘';
  return `${d}일 ${WEEKDAY_LABELS[date.getDay()]}`;
}

function signedAmount(row: FinanceRecord) {
  const amount = Number(row.amount || 0);
  if (row.type === 'EXPENSE') return -amount;
  return amount;
}

function formatSignedKRW(value: number) {
  const abs = Math.abs(value).toLocaleString('ko-KR');
  if (value > 0) return `+${abs}원`;
  if (value < 0) return `-${abs}원`;
  return `${abs}원`;
}

type DateGroup = {
  dateKey: string;
  total: number;
  items: FinanceRecord[];
};

type CategoryBreakdown = {
  category: string;
  amount: number;
  count: number;
};

function groupByCategory(rows: FinanceRecord[]): CategoryBreakdown[] {
  const map = new Map<string, CategoryBreakdown>();
  for (const row of rows) {
    const category = (row.category || '').trim() || '미분류';
    const amount = Number(row.amount || 0);
    const current = map.get(category);
    if (current) {
      current.amount += amount;
      current.count += 1;
    } else {
      map.set(category, { category, amount, count: 1 });
    }
  }
  return [...map.values()].sort((a, b) => b.amount - a.amount || a.category.localeCompare(b.category, 'ko'));
}

function normalizeCategoryName(category: string | null | undefined) {
  return (category || '').trim() || '미분류';
}

function groupRecordsByDate(rows: FinanceRecord[]): DateGroup[] {
  const groups = new Map<string, FinanceRecord[]>();
  for (const row of rows) {
    const key = getRecordDateKey(row) || '__none__';
    const list = groups.get(key);
    if (list) list.push(row);
    else groups.set(key, [row]);
  }

  return [...groups.entries()]
    .map(([dateKey, items]) => ({
      dateKey: dateKey === '__none__' ? '' : dateKey,
      items: [...items].sort((a, b) => {
        const aCash = a.cash_flow_date || '';
        const bCash = b.cash_flow_date || '';
        if (aCash !== bCash) return bCash.localeCompare(aCash);
        return b.id - a.id;
      }),
      total: items.reduce((sum, row) => {
        if (row.type === 'CAPITAL') return sum;
        return sum + signedAmount(row);
      }, 0),
    }))
    .sort((a, b) => {
      if (!a.dateKey) return 1;
      if (!b.dateKey) return -1;
      return b.dateKey.localeCompare(a.dateKey);
    });
}

export default function FinanceDashboard() {
  const requesterId = getAdminSession()?.id ?? 0;
  const [activeTab, setActiveTab] = useState<FinanceTab>('monthly');
  const [accountingView, setAccountingView] = useState<AccountingView>('income');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [recurringSubmitting, setRecurringSubmitting] = useState(false);
  const [recurringSwitchingId, setRecurringSwitchingId] = useState<number | null>(null);
  const [records, setRecords] = useState<FinanceRecord[]>([]);
  const [summary, setSummary] = useState<FinanceSummaryItem[]>([]);
  const [balance, setBalance] = useState<FinanceBalance | null>(null);
  const [recurringExpenses, setRecurringExpenses] = useState<RecurringExpense[]>([]);
  const [filter, setFilter] = useState<FinanceFilter>('ALL');
  const [selectedCategory, setSelectedCategory] = useState<SelectedCategory | null>(null);
  const [selectedMonth, setSelectedMonth] = useState(currentMonthPrefix);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState<CreateFinancePayload>(EMPTY_FORM);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [editingAttachment, setEditingAttachment] = useState<{ path: string; name: string } | null>(null);
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const [recurringForm, setRecurringForm] = useState<CreateRecurringExpensePayload>(createEmptyRecurringForm);
  const [recurringEditingId, setRecurringEditingId] = useState<number | null>(null);
  const financeCategories = getFinanceCategories(form.type);

  const loadAll = useCallback(async () => {
    if (!requesterId) return;
    setLoading(true);
    try {
      const [list, summaryRes, recurring] = await Promise.all([
        fetchFinanceList(requesterId, { month: selectedMonth }),
        fetchFinanceSummary(requesterId, 12),
        fetchRecurringExpenses(requesterId),
      ]);
      setRecords(list);
      setSummary(summaryRes.data);
      setBalance(summaryRes.balance ?? null);
      setRecurringExpenses(recurring);
    } catch (err) {
      console.error('finance load failed:', err);
      setRecords([]);
      setSummary([]);
      setBalance(null);
      setRecurringExpenses([]);
    } finally {
      setLoading(false);
    }
  }, [requesterId, selectedMonth]);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  const filteredRecords = useMemo(() => {
    if (filter === 'ALL') return records;
    return records.filter((row) => row.type === filter);
  }, [records, filter]);

  const expenseByCategory = useMemo(
    () => groupByCategory(records.filter((row) => row.type === 'EXPENSE')),
    [records]
  );

  const revenueByCategory = useMemo(
    () => groupByCategory(records.filter((row) => row.type === 'REVENUE')),
    [records]
  );

  const recordsByDate = useMemo<DateGroup[]>(
    () => groupRecordsByDate(filteredRecords),
    [filteredRecords]
  );

  const categoryFilteredRecords = useMemo(() => {
    if (!selectedCategory) return [];
    return records.filter(
      (row) =>
        row.type === selectedCategory.type &&
        normalizeCategoryName(row.category) === selectedCategory.category
    );
  }, [records, selectedCategory]);

  const categoryRecordsByDate = useMemo<DateGroup[]>(
    () => groupRecordsByDate(categoryFilteredRecords),
    [categoryFilteredRecords]
  );

  const selectedCategoryTotal = useMemo(
    () => categoryFilteredRecords.reduce((sum, row) => sum + Number(row.amount || 0), 0),
    [categoryFilteredRecords]
  );

  const monthTotals = useMemo(() => {
    const fromSummary = summary.find((row) => row.month === selectedMonth);
    if (fromSummary) {
      return {
        revenue: fromSummary.revenue,
        expense: fromSummary.expense,
        net: fromSummary.net,
      };
    }
    const revenue = records
      .filter((row) => row.type === 'REVENUE')
      .reduce((sum, row) => sum + Number(row.amount || 0), 0);
    const expense = records
      .filter((row) => row.type === 'EXPENSE')
      .reduce((sum, row) => sum + Number(row.amount || 0), 0);
    return { revenue, expense, net: revenue - expense };
  }, [summary, selectedMonth, records]);

  const chartData = useMemo(
    () => {
      const visibleSummary = summary.filter((s) => s.month >= FINANCE_DATA_START_MONTH);
      return {
        labels: visibleSummary.map((s) => s.month),
        datasets: [
          {
            label: '매출',
            data: visibleSummary.map((s) => s.revenue),
            backgroundColor: 'rgba(22, 163, 74, 0.75)',
            borderRadius: 6,
          },
          {
            label: '매입',
            data: visibleSummary.map((s) => s.expense),
            backgroundColor: 'rgba(220, 38, 38, 0.75)',
            borderRadius: 6,
          },
        ],
      };
    },
    [summary]
  );

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
      },
    },
    scales: {
      y: {
        ticks: {
          callback: (value: string | number) => `${Number(value).toLocaleString('ko-KR')}원`,
        },
      },
    },
  };

  const clearAttachmentInputs = () => {
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (imageInputRef.current) imageInputRef.current.value = '';
  };

  const resetFinanceForm = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setEditingAttachment(null);
    setAttachmentFile(null);
    clearAttachmentInputs();
  };

  const closeFinanceForm = () => {
    resetFinanceForm();
    setFormOpen(false);
  };

  const openCreateForm = () => {
    resetFinanceForm();
    setFormOpen(true);
  };

  const startEdit = (row: FinanceRecord) => {
    setEditingId(row.id);
    setEditingAttachment(
      row.attachment_path
        ? { path: row.attachment_path, name: row.attachment_name || '첨부파일' }
        : null
    );
    setAttachmentFile(null);
    clearAttachmentInputs();
    setForm({
      type: row.type,
      category: row.category,
      payment_method: row.payment_method || DEFAULT_PAYMENT_METHOD,
      amount: Number(row.amount || 0),
      description: row.description || '',
      transaction_date: row.transaction_date || '',
      cash_flow_date: row.cash_flow_date || '',
    });
    setFormOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const category = String(form.category || '').trim();
    const paymentMethod = String(form.payment_method || '').trim();
    const amount = Number(form.amount);

    if (!category || !Number.isFinite(amount) || amount <= 0) {
      alert('카테고리와 올바른 금액을 입력해 주세요.');
      return;
    }
    if (!isPaymentMethod(paymentMethod)) {
      alert('결제수단을 선택해 주세요.');
      return;
    }
    const transactionDate = String(form.transaction_date || '').trim();
    if (!transactionDate) {
      alert('날짜를 입력해 주세요.');
      return;
    }

    const payload = {
      ...form,
      category,
      payment_method: paymentMethod,
      amount,
      description: String(form.description || '').trim(),
      transaction_date: transactionDate,
      cash_flow_date: transactionDate,
    };

    setSubmitting(true);
    const uploaderEmail = getAdminSession()?.email ?? sessionStorage.getItem('user') ?? '';
    try {
      if (editingId != null) {
        await patchFinanceRecord(requesterId, editingId, payload, attachmentFile ?? undefined, { uploaderEmail });
        closeFinanceForm();
        await loadAll();
        alert('재무 내역이 수정되었습니다.');
      } else {
        await createFinanceRecord(requesterId, payload, attachmentFile ?? undefined, { uploaderEmail });
        closeFinanceForm();
        await loadAll();
        alert('재무 내역이 등록되었습니다.');
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : editingId != null ? '수정에 실패했습니다.' : '등록에 실패했습니다.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (editingId == null) return;
    if (!window.confirm('이 재무 내역을 삭제하시겠습니까?')) return;
    setDeletingId(editingId);
    try {
      await deleteFinanceRecord(requesterId, editingId);
      closeFinanceForm();
      await loadAll();
    } catch (err) {
      alert(err instanceof Error ? err.message : '삭제에 실패했습니다.');
    } finally {
      setDeletingId(null);
    }
  };

  const resetRecurringForm = () => {
    setRecurringForm(createEmptyRecurringForm());
    setRecurringEditingId(null);
  };

  const startRecurringEdit = (item: RecurringExpense) => {
    setRecurringEditingId(item.id);
    setRecurringForm({
      category: item.category,
      payment_method: item.payment_method || DEFAULT_PAYMENT_METHOD,
      amount: Number(item.amount || 0),
      repeat_day: item.repeat_day,
      start_date: item.start_date || todayDateString(),
      description: item.description || '',
    });
  };

  const handleRecurringSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const category = String(recurringForm.category || '').trim();
    const paymentMethod = String(recurringForm.payment_method || '').trim();
    const amount = Number(recurringForm.amount);
    const repeatDay = Number(recurringForm.repeat_day);
    const startDate = String(recurringForm.start_date || '').trim();

    if (!category || !Number.isFinite(amount) || amount <= 0) {
      alert('카테고리와 올바른 금액을 입력해 주세요.');
      return;
    }
    if (!isPaymentMethod(paymentMethod)) {
      alert('결제수단을 선택해 주세요.');
      return;
    }
    if (!Number.isInteger(repeatDay) || repeatDay < 1 || repeatDay > 31) {
      alert('반복일은 1~31 사이의 정수여야 합니다.');
      return;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(startDate)) {
      alert('시작날짜를 선택해 주세요.');
      return;
    }

    setRecurringSubmitting(true);
    const payload = {
      category,
      payment_method: paymentMethod,
      amount,
      repeat_day: repeatDay,
      start_date: startDate,
      description: String(recurringForm.description || '').trim(),
    };
    try {
      if (recurringEditingId != null) {
        await patchRecurringExpense(requesterId, recurringEditingId, payload);
        resetRecurringForm();
        const recurring = await fetchRecurringExpenses(requesterId);
        setRecurringExpenses(recurring);
        alert('고정비 규칙이 수정되었습니다.');
      } else {
        await createRecurringExpense(requesterId, payload);
        resetRecurringForm();
        const recurring = await fetchRecurringExpenses(requesterId);
        setRecurringExpenses(recurring);
        alert('고정비 규칙이 등록되었습니다.');
      }
    } catch (err) {
      alert(
        err instanceof Error
          ? err.message
          : recurringEditingId != null
            ? '고정비 수정에 실패했습니다.'
            : '고정비 등록에 실패했습니다.'
      );
    } finally {
      setRecurringSubmitting(false);
    }
  };

  const handleToggleRecurring = async (item: RecurringExpense) => {
    const nextIsActive: 0 | 1 = item.is_active === 1 ? 0 : 1;
    setRecurringSwitchingId(item.id);
    try {
      const updated = await patchRecurringExpense(requesterId, item.id, { is_active: nextIsActive });
      setRecurringExpenses((prev) => prev.map((row) => (row.id === item.id ? updated : row)));
    } catch (err) {
      alert(err instanceof Error ? err.message : '활성 상태 변경에 실패했습니다.');
    } finally {
      setRecurringSwitchingId(null);
    }
  };

  const switchTab = (tab: FinanceTab) => {
    setActiveTab(tab);
    setSelectedCategory(null);
    if (tab !== 'recurring') {
      resetRecurringForm();
    }
  };

  const renderDayRecordList = (groups: DateGroup[], emptyMessage: string) => {
    if (loading) {
      return <p className="finance-dashboard__empty">불러오는 중…</p>;
    }
    if (groups.length === 0) {
      return <p className="finance-dashboard__empty">{emptyMessage}</p>;
    }
    return (
      <div className="finance-dashboard__day-list">
        <div className="finance-dashboard__day-cols finance-dashboard__day-cols--head" aria-hidden="true">
          <span>구분</span>
          <span>카테고리</span>
          <span>메모</span>
          <span>입출금일</span>
          <span>금액</span>
          <span>결제수단</span>
          <span>첨부파일</span>
          <span>관리</span>
        </div>
        {groups.map((group) => (
          <section key={group.dateKey || 'none'} className="finance-dashboard__day-group">
            <div className="finance-dashboard__day-header">
              <span className="finance-dashboard__day-label">{formatDayHeader(group.dateKey)}</span>
              <span
                className={`finance-dashboard__day-total${
                  group.total > 0
                    ? ' finance-dashboard__day-total--plus'
                    : group.total < 0
                      ? ' finance-dashboard__day-total--minus'
                      : ''
                }`}
              >
                {formatSignedKRW(group.total)}
              </span>
            </div>
            <ul className="finance-dashboard__day-items">
              {group.items.map((row) => {
                const signed = signedAmount(row);
                const isRecurring = isAutoRecurringRecord(row.description);
                const memo = formatFinanceRecordMemo(row.description);

                return (
                  <li
                    key={row.id}
                    className={`finance-dashboard__day-item finance-dashboard__day-cols${
                      editingId === row.id ? ' finance-dashboard__day-item--editing' : ''
                    }`}
                  >
                    <span
                      className={`finance-dashboard__type-badge finance-dashboard__day-cell--type ${getFinanceTypeBadgeClass(row.type)}`}
                      data-label="구분"
                    >
                      {getFinanceTypeLabel(row.type)}
                    </span>
                    <span className="finance-dashboard__day-cell finance-dashboard__day-cell--category" data-label="카테고리">
                      {isRecurring && (
                        <span className="finance-dashboard__recurring-badge">고정비</span>
                      )}
                      {row.category}
                    </span>
                    <span
                      className={`finance-dashboard__day-cell finance-dashboard__day-cell--memo${
                        memo ? '' : ' is-empty'
                      }`}
                      data-label="메모"
                    >
                      {memo || '—'}
                    </span>
                    <span className="finance-dashboard__day-cell finance-dashboard__day-cell--date" data-label="입출금일">
                      {row.cash_flow_date || '—'}
                    </span>
                    <span
                      className={`finance-dashboard__day-amount finance-dashboard__day-cell--amount ${getFinanceAmountClass(row.type)}`}
                      data-label="금액"
                    >
                      {row.type === 'CAPITAL'
                        ? numberKRW(Number(row.amount || 0))
                        : formatSignedKRW(signed)}
                    </span>
                    <span
                      className={`finance-dashboard__day-cell finance-dashboard__day-cell--payment${
                        row.payment_method ? '' : ' is-empty'
                      }`}
                      data-label="결제수단"
                    >
                      {row.payment_method || '—'}
                    </span>
                    <span
                      className={`finance-dashboard__day-cell finance-dashboard__day-cell--attach${
                        row.attachment_path ? '' : ' is-empty'
                      }`}
                      data-label="첨부파일"
                    >
                      {row.attachment_path ? (
                        <a
                          href={getFinanceAttachmentUrl(row.attachment_path)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="finance-dashboard__attachment-link"
                        >
                          {row.attachment_name || '첨부파일'}
                        </a>
                      ) : (
                        '—'
                      )}
                    </span>
                    <span className="finance-dashboard__day-cell finance-dashboard__day-cell--action" data-label="관리">
                      <button
                        type="button"
                        className="finance-dashboard__edit-btn"
                        disabled={submitting && editingId === row.id}
                        onClick={() => startEdit(row)}
                      >
                        {editingId === row.id && formOpen ? '수정 중' : '수정'}
                      </button>
                    </span>
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
      </div>
    );
  };

  const renderCategoryBreakdownList = (
    items: CategoryBreakdown[],
    type: 'REVENUE' | 'EXPENSE',
    total: number,
    emptyMessage: string
  ) => {
    if (items.length === 0) {
      return <p className="finance-dashboard__empty">{emptyMessage}</p>;
    }
    return (
      <ul className="finance-dashboard__analysis-list">
        {items.map((item) => {
          const pct = total > 0 ? (item.amount / total) * 100 : 0;
          return (
            <li key={item.category} className="finance-dashboard__analysis-item">
              <button
                type="button"
                className="finance-dashboard__category-item-btn"
                onClick={() => setSelectedCategory({ category: item.category, type })}
              >
                <div className="finance-dashboard__analysis-item-row">
                  <span className="finance-dashboard__analysis-item-name">{item.category}</span>
                  <span
                    className={`finance-dashboard__analysis-item-amount ${
                      type === 'EXPENSE'
                        ? 'finance-dashboard__amount--expense'
                        : 'finance-dashboard__amount--revenue'
                    }`}
                  >
                    {numberKRW(item.amount)}
                  </span>
                </div>
                <div
                  className="finance-dashboard__analysis-bar"
                  role="progressbar"
                  aria-valuenow={Math.round(pct)}
                  aria-valuemin={0}
                  aria-valuemax={100}
                  aria-label={`${item.category} ${pct.toFixed(1)}%`}
                >
                  <span
                    className={`finance-dashboard__analysis-bar-fill ${
                      type === 'EXPENSE'
                        ? 'finance-dashboard__analysis-bar-fill--expense'
                        : 'finance-dashboard__analysis-bar-fill--revenue'
                    }`}
                    style={{ width: `${Math.max(pct, pct > 0 ? 2 : 0)}%` }}
                  />
                </div>
                <p className="finance-dashboard__analysis-item-meta">
                  {pct.toFixed(1)}% · {item.count}건
                </p>
              </button>
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <div className="finance-dashboard">
      <div className="finance-dashboard__tabs" role="tablist" aria-label="재무관리 메뉴">
        <button
          type="button"
          role="tab"
          id="finance-tab-monthly"
          aria-selected={activeTab === 'monthly'}
          aria-controls="finance-panel-monthly"
          className={`finance-dashboard__tab${activeTab === 'monthly' ? ' is-active' : ''}`}
          onClick={() => switchTab('monthly')}
        >
          월별 재무내역
        </button>
        <button
          type="button"
          role="tab"
          id="finance-tab-category"
          aria-selected={activeTab === 'category'}
          aria-controls="finance-panel-category"
          className={`finance-dashboard__tab${activeTab === 'category' ? ' is-active' : ''}`}
          onClick={() => switchTab('category')}
        >
          카테고리별 재무내역
        </button>
        <button
          type="button"
          role="tab"
          id="finance-tab-recurring"
          aria-selected={activeTab === 'recurring'}
          aria-controls="finance-panel-recurring"
          className={`finance-dashboard__tab${activeTab === 'recurring' ? ' is-active' : ''}`}
          onClick={() => switchTab('recurring')}
        >
          고정비 관리
        </button>
        <button
          type="button"
          role="tab"
          id="finance-tab-accounting"
          aria-selected={activeTab === 'accounting'}
          aria-controls="finance-panel-accounting"
          className={`finance-dashboard__tab${activeTab === 'accounting' ? ' is-active' : ''}`}
          onClick={() => switchTab('accounting')}
        >
          회계관리
        </button>
      </div>

      {activeTab === 'accounting' && (
        <div
          className="finance-dashboard__tab-panel"
          id="finance-panel-accounting"
          role="tabpanel"
          aria-labelledby="finance-tab-accounting"
        >
          <div className="finance-dashboard__panel-toolbar">
            <div className="finance-dashboard__accounting-switch" role="group" aria-label="회계 보고서 선택">
              <button
                type="button"
                className={`finance-dashboard__accounting-switch-btn${
                  accountingView === 'income' ? ' is-active' : ''
                }`}
                onClick={() => setAccountingView('income')}
              >
                손익계산서
              </button>
              <button
                type="button"
                className={`finance-dashboard__accounting-switch-btn${
                  accountingView === 'balance' ? ' is-active' : ''
                }`}
                onClick={() => setAccountingView('balance')}
              >
                재무상태표
              </button>
            </div>
            {accountingView === 'income' && (
              <div className="finance-dashboard__month-nav">
                <button
                  type="button"
                  className="finance-dashboard__month-nav-btn"
                  onClick={() => setSelectedMonth((prev) => shiftMonth(prev, -1))}
                  disabled={loading || !canShiftMonthBack(selectedMonth)}
                >
                  ‹
                </button>
                <span className="finance-dashboard__month-label">
                  {formatMonthLabel(selectedMonth)}
                </span>
                <button
                  type="button"
                  className="finance-dashboard__month-nav-btn"
                  onClick={() => setSelectedMonth((prev) => shiftMonth(prev, 1))}
                  disabled={loading}
                >
                  ›
                </button>
                <button
                  type="button"
                  className="finance-dashboard__month-today-btn"
                  onClick={() => setSelectedMonth(currentMonthPrefix())}
                  disabled={loading}
                >
                  이번 달
                </button>
              </div>
            )}
          </div>

          {accountingView === 'income' ? (
            <section className="finance-dashboard__statement-panel">
              <h3 className="finance-dashboard__section-title">
                {formatMonthLabel(selectedMonth)} 손익계산서
              </h3>
              {loading ? (
                <p className="finance-dashboard__empty">불러오는 중…</p>
              ) : (
                <div className="finance-dashboard__table-wrap">
                  <table className="finance-dashboard__statement-table">
                    <tbody>
                      <tr className="finance-dashboard__statement-row--section">
                        <td>Ⅰ. 매출액</td>
                        <td className="finance-dashboard__amount--revenue">
                          {numberKRW(monthTotals.revenue)}
                        </td>
                      </tr>
                      {revenueByCategory.length === 0 ? (
                        <tr className="finance-dashboard__statement-row--item">
                          <td className="finance-dashboard__statement-cell--indent">매출 내역 없음</td>
                          <td>—</td>
                        </tr>
                      ) : (
                        revenueByCategory.map((item) => (
                          <tr key={`rev-${item.category}`} className="finance-dashboard__statement-row--item">
                            <td className="finance-dashboard__statement-cell--indent">{item.category}</td>
                            <td>{numberKRW(item.amount)}</td>
                          </tr>
                        ))
                      )}

                      <tr className="finance-dashboard__statement-row--section">
                        <td>Ⅱ. 매입원가(비용)</td>
                        <td className="finance-dashboard__amount--expense">
                          {numberKRW(monthTotals.expense)}
                        </td>
                      </tr>
                      {expenseByCategory.length === 0 ? (
                        <tr className="finance-dashboard__statement-row--item">
                          <td className="finance-dashboard__statement-cell--indent">매입 내역 없음</td>
                          <td>—</td>
                        </tr>
                      ) : (
                        expenseByCategory.map((item) => (
                          <tr key={`exp-${item.category}`} className="finance-dashboard__statement-row--item">
                            <td className="finance-dashboard__statement-cell--indent">{item.category}</td>
                            <td>{numberKRW(item.amount)}</td>
                          </tr>
                        ))
                      )}

                      <tr className="finance-dashboard__statement-row--total">
                        <td>Ⅲ. 영업이익(당기순이익)</td>
                        <td
                          className={
                            monthTotals.net >= 0
                              ? 'finance-dashboard__amount--revenue'
                              : 'finance-dashboard__amount--expense'
                          }
                        >
                          {numberKRW(monthTotals.net)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          ) : (
            <section className="finance-dashboard__statement-panel">
              <h3 className="finance-dashboard__section-title">
                재무상태표{' '}
                <span className="finance-dashboard__hint">(오늘 기준 누적)</span>
              </h3>
              {loading || !balance ? (
                <p className="finance-dashboard__empty">불러오는 중…</p>
              ) : (
                <div className="finance-dashboard__balance-sheet">
                  <div className="finance-dashboard__balance-sheet-col">
                    <h4 className="finance-dashboard__balance-sheet-title">자산</h4>
                    <table className="finance-dashboard__statement-table">
                      <tbody>
                        <tr className="finance-dashboard__statement-row--item">
                          <td className="finance-dashboard__statement-cell--indent">현금성자산</td>
                          <td>{numberKRW(balance.total)}</td>
                        </tr>
                        <tr className="finance-dashboard__statement-row--total">
                          <td>자산총계</td>
                          <td>{numberKRW(balance.total)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="finance-dashboard__balance-sheet-col">
                    <h4 className="finance-dashboard__balance-sheet-title">부채와 자본</h4>
                    <table className="finance-dashboard__statement-table">
                      <tbody>
                        <tr className="finance-dashboard__statement-row--item">
                          <td className="finance-dashboard__statement-cell--indent">부채총계</td>
                          <td>{numberKRW(0)}</td>
                        </tr>
                        <tr className="finance-dashboard__statement-row--section">
                          <td>자본</td>
                          <td>{numberKRW(balance.capital + balance.revenue - balance.expense)}</td>
                        </tr>
                        <tr className="finance-dashboard__statement-row--item">
                          <td className="finance-dashboard__statement-cell--indent">자본금</td>
                          <td>{numberKRW(balance.capital)}</td>
                        </tr>
                        <tr className="finance-dashboard__statement-row--item">
                          <td className="finance-dashboard__statement-cell--indent">이익잉여금(누적순이익)</td>
                          <td>{numberKRW(balance.revenue - balance.expense)}</td>
                        </tr>
                        <tr className="finance-dashboard__statement-row--total">
                          <td>부채와자본총계</td>
                          <td>{numberKRW(balance.capital + balance.revenue - balance.expense)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </section>
          )}
        </div>
      )}

      {activeTab === 'monthly' && (
        <div
          className="finance-dashboard__tab-panel"
          id="finance-panel-monthly"
          role="tabpanel"
          aria-labelledby="finance-tab-monthly"
        >
          <div className="finance-dashboard__panel-toolbar">
            <div className="finance-dashboard__month-nav">
              <button
                type="button"
                className="finance-dashboard__month-nav-btn"
                onClick={() => setSelectedMonth((prev) => shiftMonth(prev, -1))}
                disabled={loading || !canShiftMonthBack(selectedMonth)}
              >
                ‹
              </button>
              <span className="finance-dashboard__month-label">
                {formatMonthLabel(selectedMonth)}
              </span>
              <button
                type="button"
                className="finance-dashboard__month-nav-btn"
                onClick={() => setSelectedMonth((prev) => shiftMonth(prev, 1))}
                disabled={loading}
              >
                ›
              </button>
              <button
                type="button"
                className="finance-dashboard__month-today-btn"
                onClick={() => setSelectedMonth(currentMonthPrefix())}
                disabled={loading}
              >
                이번 달
              </button>
            </div>
            <div className="finance-dashboard__panel-toolbar-actions">
              <select
                className="finance-dashboard__filter-select"
                value={filter}
                onChange={(e) => setFilter(e.target.value as FinanceFilter)}
              >
                <option value="ALL">전체</option>
                {FINANCE_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="finance-dashboard__register-btn"
                onClick={openCreateForm}
              >
                등록
              </button>
            </div>
          </div>

          <div className="finance-dashboard__cards">
            <article className="finance-dashboard__card">
              <p className="finance-dashboard__card-label">{formatMonthLabel(selectedMonth)} 매출</p>
              <p className="finance-dashboard__card-value finance-dashboard__card-value--revenue">
                {numberKRW(monthTotals.revenue)}
              </p>
            </article>
            <article className="finance-dashboard__card">
              <p className="finance-dashboard__card-label">{formatMonthLabel(selectedMonth)} 매입</p>
              <p className="finance-dashboard__card-value finance-dashboard__card-value--expense">
                {numberKRW(monthTotals.expense)}
              </p>
            </article>
            <article className="finance-dashboard__card">
              <p className="finance-dashboard__card-label">{formatMonthLabel(selectedMonth)} 순이익</p>
              <p className="finance-dashboard__card-value">{numberKRW(monthTotals.net)}</p>
            </article>
            {balance && (
              <article className="finance-dashboard__card finance-dashboard__balance-card">
                <p className="finance-dashboard__card-label">총 잔액 (자본금 + 매출 − 매입)</p>
                <p
                  className={`finance-dashboard__card-value finance-dashboard__card-value--capital${
                    balance.total < 0 ? ' finance-dashboard__card-value--expense' : ''
                  }`}
                >
                  {numberKRW(balance.total)}
                </p>
              </article>
            )}
          </div>

          <section className="finance-dashboard__table-panel">
            <h3 className="finance-dashboard__section-title">
              {formatMonthLabel(selectedMonth)} 거래 내역
            </h3>
            {renderDayRecordList(
              recordsByDate,
              `${formatMonthLabel(selectedMonth)} 거래 내역이 없습니다.`
            )}
          </section>

          <section className="finance-dashboard__monthly-table-panel">
            <h3 className="finance-dashboard__section-title">월별 통계</h3>
            {summary.length === 0 ? (
              <p className="finance-dashboard__empty">월별 통계 데이터가 없습니다.</p>
            ) : (
              <div className="finance-dashboard__table-wrap">
                <table className="finance-dashboard__table finance-dashboard__table--monthly">
                  <thead>
                    <tr>
                      <th>월</th>
                      <th>매출</th>
                      <th>매입</th>
                      <th>순이익</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...summary]
                      .filter((item) => item.month >= FINANCE_DATA_START_MONTH)
                      .reverse()
                      .map((item) => (
                      <tr
                        key={item.month}
                        className={item.month === selectedMonth ? 'finance-dashboard__month-row--selected' : ''}
                      >
                        <td data-label="월">
                          <button
                            type="button"
                            className="finance-dashboard__month-link"
                            onClick={() => setSelectedMonth(item.month)}
                          >
                            {formatMonthLabel(item.month)}
                          </button>
                        </td>
                        <td className="finance-dashboard__amount--revenue" data-label="매출">
                          {numberKRW(item.revenue)}
                        </td>
                        <td className="finance-dashboard__amount--expense" data-label="매입">
                          {numberKRW(item.expense)}
                        </td>
                        <td
                          className={
                            item.net >= 0
                              ? 'finance-dashboard__amount--revenue'
                              : 'finance-dashboard__amount--expense'
                          }
                          data-label="순이익"
                        >
                          {numberKRW(item.net)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="finance-dashboard__chart-panel">
            <h3 className="finance-dashboard__section-title">월별 매입/매출 비교</h3>
            {summary.length === 0 ? (
              <p className="finance-dashboard__empty">월별 통계 데이터가 없습니다.</p>
            ) : (
              <div className="finance-dashboard__chart-wrap">
                <div className="finance-dashboard__chart-inner">
                  <Bar data={chartData} options={chartOptions} />
                </div>
              </div>
            )}
          </section>
        </div>
      )}

      {activeTab === 'category' && (
        <div
          className="finance-dashboard__tab-panel"
          id="finance-panel-category"
          role="tabpanel"
          aria-labelledby="finance-tab-category"
        >
          <div className="finance-dashboard__panel-toolbar">
            <div className="finance-dashboard__month-nav">
              <button
                type="button"
                className="finance-dashboard__month-nav-btn"
                onClick={() => {
                  setSelectedCategory(null);
                  setSelectedMonth((prev) => shiftMonth(prev, -1));
                }}
                disabled={loading || !canShiftMonthBack(selectedMonth)}
              >
                ‹
              </button>
              <span className="finance-dashboard__month-label">
                {formatMonthLabel(selectedMonth)}
              </span>
              <button
                type="button"
                className="finance-dashboard__month-nav-btn"
                onClick={() => {
                  setSelectedCategory(null);
                  setSelectedMonth((prev) => shiftMonth(prev, 1));
                }}
                disabled={loading}
              >
                ›
              </button>
              <button
                type="button"
                className="finance-dashboard__month-today-btn"
                onClick={() => {
                  setSelectedCategory(null);
                  setSelectedMonth(currentMonthPrefix());
                }}
                disabled={loading}
              >
                이번 달
              </button>
            </div>
          </div>

          {selectedCategory ? (
            <section className="finance-dashboard__table-panel">
              <div className="finance-dashboard__category-detail-head">
                <button
                  type="button"
                  className="finance-dashboard__category-back-btn"
                  onClick={() => setSelectedCategory(null)}
                >
                  ← 카테고리 목록
                </button>
                <div className="finance-dashboard__category-detail-meta">
                  <h3 className="finance-dashboard__section-title">
                    {selectedCategory.category}
                  </h3>
                  <p className="finance-dashboard__category-detail-summary">
                    <span
                      className={`finance-dashboard__type-badge ${getFinanceTypeBadgeClass(selectedCategory.type)}`}
                    >
                      {getFinanceTypeLabel(selectedCategory.type)}
                    </span>
                    <span
                      className={
                        selectedCategory.type === 'EXPENSE'
                          ? 'finance-dashboard__amount--expense'
                          : 'finance-dashboard__amount--revenue'
                      }
                    >
                      {numberKRW(selectedCategoryTotal)}
                    </span>
                    <span className="finance-dashboard__category-detail-count">
                      {categoryFilteredRecords.length}건
                    </span>
                  </p>
                </div>
              </div>
              {renderDayRecordList(
                categoryRecordsByDate,
                `${formatMonthLabel(selectedMonth)} · ${selectedCategory.category} 내역이 없습니다.`
              )}
            </section>
          ) : (
            <section className="finance-dashboard__analysis-panel">
              <h3 className="finance-dashboard__section-title">
                {formatMonthLabel(selectedMonth)} 카테고리별 재무내역
              </h3>
              <p className="finance-dashboard__hint">
                카테고리를 선택하면 해당 항목만 날짜별로 확인할 수 있습니다.
              </p>
              {loading ? (
                <p className="finance-dashboard__empty">불러오는 중…</p>
              ) : (
                <div className="finance-dashboard__analysis-groups">
                  <div className="finance-dashboard__analysis-group">
                    <div className="finance-dashboard__analysis-group-head">
                      <h4 className="finance-dashboard__analysis-group-title">매입</h4>
                      <span className="finance-dashboard__analysis-group-total finance-dashboard__amount--expense">
                        {numberKRW(monthTotals.expense)}
                      </span>
                    </div>
                    {renderCategoryBreakdownList(
                      expenseByCategory,
                      'EXPENSE',
                      monthTotals.expense,
                      '매입 내역이 없습니다.'
                    )}
                  </div>
                  <div className="finance-dashboard__analysis-group">
                    <div className="finance-dashboard__analysis-group-head">
                      <h4 className="finance-dashboard__analysis-group-title">매출</h4>
                      <span className="finance-dashboard__analysis-group-total finance-dashboard__amount--revenue">
                        {numberKRW(monthTotals.revenue)}
                      </span>
                    </div>
                    {renderCategoryBreakdownList(
                      revenueByCategory,
                      'REVENUE',
                      monthTotals.revenue,
                      '매출 내역이 없습니다.'
                    )}
                  </div>
                </div>
              )}
            </section>
          )}
        </div>
      )}

      {activeTab === 'recurring' && (
        <div
          className="finance-dashboard__tab-panel"
          id="finance-panel-recurring"
          role="tabpanel"
          aria-labelledby="finance-tab-recurring"
        >
          <section className="finance-dashboard__recurring-panel">
            <div className="finance-dashboard__recurring-head">
              <h3 className="finance-dashboard__section-title">고정비 관리</h3>
              <p className="finance-dashboard__hint">매월 반복되는 매입 항목을 설정하고 활성 상태를 관리할 수 있습니다.</p>
            </div>

            <form onSubmit={handleRecurringSubmit}>
              <h4 className="finance-dashboard__recurring-form-title">
                {recurringEditingId != null ? '고정비 수정' : '고정비 등록'}
              </h4>
              <div className="finance-dashboard__form-grid finance-dashboard__form-grid--recurring">
                <div className="finance-dashboard__field finance-dashboard__field--wide">
                  <label htmlFor="recurring-category">카테고리</label>
                  <div id="recurring-category" className="finance-dashboard__chips" role="group" aria-label="고정비 카테고리 선택">
                    {EXPENSE_CATEGORIES.map((category) => (
                      <button
                        key={category}
                        type="button"
                        className={`finance-dashboard__chip${
                          recurringForm.category === category ? ' is-selected' : ''
                        }`}
                        onClick={() => setRecurringForm((prev) => ({ ...prev, category }))}
                      >
                        {category}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="finance-dashboard__field">
                  <label htmlFor="recurring-amount">금액</label>
                  <input
                    id="recurring-amount"
                    type="text"
                    inputMode="numeric"
                    value={formatNumberInput(Number(recurringForm.amount || 0))}
                    onChange={(e) =>
                      setRecurringForm((prev) => ({ ...prev, amount: parseNumberInput(e.target.value) }))
                    }
                    placeholder="0"
                  />
                </div>
                <div className="finance-dashboard__field">
                  <label htmlFor="recurring-payment-method">결제수단</label>
                  <select
                    id="recurring-payment-method"
                    value={recurringForm.payment_method}
                    onChange={(e) =>
                      setRecurringForm((prev) => ({ ...prev, payment_method: e.target.value }))
                    }
                  >
                    {PAYMENT_METHODS.map((method) => (
                      <option key={method} value={method}>
                        {method}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="finance-dashboard__field">
                  <label htmlFor="recurring-repeat-day">매월 반복일</label>
                  <input
                    id="recurring-repeat-day"
                    type="number"
                    min={1}
                    max={31}
                    value={recurringForm.repeat_day}
                    onChange={(e) =>
                      setRecurringForm((prev) => ({ ...prev, repeat_day: Number(e.target.value || 1) }))
                    }
                  />
                </div>
                <div className="finance-dashboard__field">
                  <label htmlFor="recurring-start-date">시작날짜</label>
                  <input
                    id="recurring-start-date"
                    type="date"
                    min={`${FINANCE_DATA_START_MONTH}-01`}
                    value={recurringForm.start_date || ''}
                    onChange={(e) =>
                      setRecurringForm((prev) => ({ ...prev, start_date: e.target.value }))
                    }
                  />
                </div>
                <div className="finance-dashboard__field finance-dashboard__field--wide">
                  <label htmlFor="recurring-description">메모</label>
                  <textarea
                    id="recurring-description"
                    value={recurringForm.description || ''}
                    onChange={(e) => setRecurringForm((prev) => ({ ...prev, description: e.target.value }))}
                    placeholder="고정비에 대한 설명을 입력하세요."
                  />
                </div>
              </div>
              <div className="finance-dashboard__form-actions">
                {recurringEditingId != null && (
                  <button
                    className="finance-dashboard__cancel-btn"
                    type="button"
                    disabled={recurringSubmitting}
                    onClick={resetRecurringForm}
                  >
                    수정 취소
                  </button>
                )}
                <button className="finance-dashboard__submit-btn" type="submit" disabled={recurringSubmitting}>
                  {recurringSubmitting
                    ? recurringEditingId != null
                      ? '수정 중…'
                      : '등록 중…'
                    : recurringEditingId != null
                      ? '고정비 수정'
                      : '고정비 등록'}
                </button>
              </div>
            </form>

            {loading ? (
              <p className="finance-dashboard__empty">고정비 목록을 불러오는 중…</p>
            ) : recurringExpenses.length === 0 ? (
              <p className="finance-dashboard__empty">등록된 고정비 규칙이 없습니다.</p>
            ) : (
              <div className="finance-dashboard__table-wrap">
                <table className="finance-dashboard__table finance-dashboard__table--recurring">
                  <thead>
                    <tr>
                      <th>카테고리</th>
                      <th>금액</th>
                      <th>결제수단</th>
                      <th>반복일</th>
                      <th>시작날짜</th>
                      <th>메모</th>
                      <th>활성화</th>
                      <th>관리</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recurringExpenses.map((item) => (
                      <tr
                        key={item.id}
                        className={recurringEditingId === item.id ? 'finance-dashboard__row--editing' : ''}
                      >
                        <td data-label="카테고리">{item.category}</td>
                        <td className="finance-dashboard__amount--expense" data-label="금액">
                          {numberKRW(Number(item.amount || 0))}
                        </td>
                        <td data-label="결제수단">{item.payment_method || DEFAULT_PAYMENT_METHOD}</td>
                        <td data-label="반복일">매월 {item.repeat_day}일</td>
                        <td data-label="시작날짜">{item.start_date || '—'}</td>
                        <td data-label="메모">{item.description || '—'}</td>
                        <td data-label="활성화">
                          <button
                            type="button"
                            className={`finance-dashboard__switch${item.is_active === 1 ? ' is-on' : ''}`}
                            onClick={() => void handleToggleRecurring(item)}
                            disabled={recurringSwitchingId === item.id}
                            aria-label={`고정비 ${item.category} 활성화 토글`}
                          >
                            <span className="finance-dashboard__switch-thumb" />
                          </button>
                        </td>
                        <td data-label="관리">
                          <button
                            type="button"
                            className="finance-dashboard__edit-btn"
                            disabled={recurringSubmitting && recurringEditingId === item.id}
                            onClick={() => startRecurringEdit(item)}
                          >
                            {recurringEditingId === item.id ? '수정 중' : '수정'}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      )}

      {formOpen && (
        <div
          className="finance-dashboard__modal-backdrop"
          role="presentation"
          onClick={(e) => {
            if (e.target === e.currentTarget && !submitting) closeFinanceForm();
          }}
        >
          <div
            className="finance-dashboard__modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="finance-form-title"
          >
            <div className="finance-dashboard__modal-header">
              <h2 id="finance-form-title" className="finance-dashboard__modal-title">
                {editingId != null ? '재무 내역 수정' : '재무 내역 등록'}
              </h2>
              <button
                type="button"
                className="finance-dashboard__modal-close"
                onClick={closeFinanceForm}
                disabled={submitting}
                aria-label="닫기"
              >
                ×
              </button>
            </div>
            <form onSubmit={handleSubmit} className="finance-dashboard__modal-body">
              <div className="finance-dashboard__form-grid">
                <div className="finance-dashboard__field finance-dashboard__field--wide">
                  <label htmlFor="finance-amount">금액</label>
                  <input
                    id="finance-amount"
                    type="text"
                    inputMode="numeric"
                    value={formatNumberInput(Number(form.amount || 0))}
                    onChange={(e) => setForm((prev) => ({ ...prev, amount: parseNumberInput(e.target.value) }))}
                    placeholder="0"
                  />
                </div>
                <div className="finance-dashboard__field finance-dashboard__field--wide">
                  <label htmlFor="finance-type">구분</label>
                  <div id="finance-type" className="finance-dashboard__chips" role="group" aria-label="구분 선택">
                    {FINANCE_TYPE_OPTIONS.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        className={`finance-dashboard__chip${form.type === option.value ? ' is-selected' : ''}`}
                        onClick={() =>
                          setForm((prev) => ({
                            ...prev,
                            type: option.value,
                            category: prev.type === option.value ? prev.category : '',
                          }))
                        }
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="finance-dashboard__field finance-dashboard__field--wide">
                  <label htmlFor="finance-category">카테고리</label>
                  <div
                    id="finance-category"
                    className="finance-dashboard__chips finance-dashboard__chips--wrap"
                    role="group"
                    aria-label="카테고리 선택"
                  >
                    {financeCategories.map((category) => (
                      <button
                        key={category}
                        type="button"
                        className={`finance-dashboard__chip${form.category === category ? ' is-selected' : ''}`}
                        onClick={() => setForm((prev) => ({ ...prev, category }))}
                      >
                        {category}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="finance-dashboard__field">
                  <label htmlFor="finance-payment-method">결제수단</label>
                  <select
                    id="finance-payment-method"
                    value={form.payment_method}
                    onChange={(e) => setForm((prev) => ({ ...prev, payment_method: e.target.value }))}
                  >
                    {PAYMENT_METHODS.map((method) => (
                      <option key={method} value={method}>
                        {method}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="finance-dashboard__field">
                  <label htmlFor="finance-transaction-date">날짜 *</label>
                  <input
                    id="finance-transaction-date"
                    type="date"
                    required
                    value={form.transaction_date || ''}
                    onChange={(e) =>
                      setForm((prev) => ({
                        ...prev,
                        transaction_date: e.target.value,
                        cash_flow_date: e.target.value,
                      }))
                    }
                  />
                </div>
                <div className="finance-dashboard__field finance-dashboard__field--wide">
                  <label htmlFor="finance-description">메모</label>
                  <textarea
                    id="finance-description"
                    value={form.description || ''}
                    onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                    placeholder="상세 메모를 입력하세요."
                  />
                </div>
                <div className="finance-dashboard__field finance-dashboard__field--wide">
                  <span className="finance-dashboard__file-label">첨부파일 (선택 · 최대 10MB)</span>
                  <div className="finance-dashboard__file-upload">
                    <input
                      id="finance-attachment-image"
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      ref={imageInputRef}
                      onChange={(e) => setAttachmentFile(e.target.files?.[0] ?? null)}
                      className="finance-dashboard__file-input"
                    />
                    <input
                      id="finance-attachment-file"
                      type="file"
                      accept="application/pdf,.pdf"
                      ref={fileInputRef}
                      onChange={(e) => setAttachmentFile(e.target.files?.[0] ?? null)}
                      className="finance-dashboard__file-input"
                    />
                    <label
                      htmlFor="finance-attachment-image"
                      className="finance-dashboard__file-btn finance-dashboard__file-btn--image"
                    >
                      이미지 선택
                    </label>
                    <label htmlFor="finance-attachment-file" className="finance-dashboard__file-btn">
                      파일 선택
                    </label>
                    <span className="finance-dashboard__file-name">
                      {attachmentFile
                        ? attachmentFile.name
                        : editingAttachment
                          ? `현재: ${editingAttachment.name}`
                          : '선택된 파일 없음'}
                    </span>
                  </div>
                  {editingAttachment && !attachmentFile && (
                    <span className="finance-dashboard__file-hint">
                      <a
                        href={getFinanceAttachmentUrl(editingAttachment.path)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="finance-dashboard__attachment-link"
                      >
                        기존 첨부파일 보기
                      </a>
                      {' '}· 새 파일 선택 시 교체됩니다
                    </span>
                  )}
                </div>
              </div>
              <div className="finance-dashboard__form-actions">
                {editingId != null && (
                  <button
                    type="button"
                    className="finance-dashboard__delete-btn"
                    disabled={deletingId === editingId || submitting}
                    onClick={() => void handleDelete()}
                  >
                    {deletingId === editingId ? '삭제 중…' : '내역 삭제'}
                  </button>
                )}
                <button
                  className="finance-dashboard__cancel-btn"
                  type="button"
                  disabled={submitting}
                  onClick={closeFinanceForm}
                >
                  취소
                </button>
                <button className="finance-dashboard__submit-btn" type="submit" disabled={submitting}>
                  {submitting
                    ? editingId != null
                      ? '수정 중…'
                      : '등록 중…'
                    : editingId != null
                      ? '내역 수정'
                      : '내역 등록'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
